/**
 * A YAML parser for the common subset of YAML 1.2 (core schema), written for the
 * converter. It reads block and flow collections, plain, quoted and block scalars,
 * comments, multiple documents, anchors and aliases, the `<<` merge key, and the core
 * tags. It does not read complex keys (`? `), `%TAG` directives, custom tag handlers,
 * binary/timestamp/set types, or YAML 1.1 extras such as sexagesimal numbers.
 * Everything else is reported with its line and column, never guessed at.
 */

import {
  DataError,
  type DataMap,
  type DataValue,
  type DataWarning,
  isMap,
} from "@/lib/tool-kit/latex-dev/data-model";
import {
  type FlowHost,
  FlowReader,
  flowComplete,
} from "@/lib/tool-kit/latex-dev/yaml-flow";
import {
  coreTag,
  indentOf,
  isBlankLine,
  isCommentLine,
  type Line,
  mappingColon,
  type Props,
  parseProps,
  stripComment,
} from "@/lib/tool-kit/latex-dev/yaml-lines";
import {
  keyToString,
  type ResolvedScalar,
  resolvePlain,
} from "@/lib/tool-kit/latex-dev/yaml-scalar";
import {
  assembleBlock,
  type Fail,
  parseBlockHeader,
  quotedEnd,
  readDoubleQuoted,
  readSingleQuoted,
} from "@/lib/tool-kit/latex-dev/yaml-text";

export interface YamlParseOptions {
  /** Apply `<<` merge keys (a YAML 1.1 convention most tools support). */
  merge: boolean;
}

export interface YamlParseResult {
  documents: DataValue[];
  warnings: DataWarning[];
}

type Params = Record<string, string | number>;

const RE_DOC_START = /^---(?:[ \t]|$)/;
const RE_DOC_END = /^\.\.\.(?:[ \t]|$)/;
const RE_ALIAS = /^\*[^\s,[\]{}]+$/;
const RE_VERSION = /^%YAML[ \t]/;
const RE_BOM = /^﻿/;
const RE_NEWLINES = /\r\n?/g;
const RE_WHITESPACE = /\s/;

function isExplicitKey(content: string): boolean {
  return (
    content === "?" || content.startsWith("? ") || content.startsWith("?\t")
  );
}

function isItem(content: string): boolean {
  return (
    content === "-" || content.startsWith("- ") || content.startsWith("-\t")
  );
}

class DocumentParser implements FlowHost {
  private readonly lines: Line[];
  private pos = 0;
  private readonly anchors = new Map<string, DataValue>();
  private readonly options: YamlParseOptions;
  private readonly warnings: DataWarning[];

  constructor(
    lines: Line[],
    options: YamlParseOptions,
    warnings: DataWarning[]
  ) {
    this.lines = lines;
    this.options = options;
    this.warnings = warnings;
  }

  parse(): DataValue {
    this.skipBlank();
    if (this.pos >= this.lines.length) {
      return null;
    }
    const value = this.parseBlock(-1);
    this.skipBlank();
    if (this.pos < this.lines.length) {
      const line = this.lines[this.pos];
      throw new DataError(
        "unexpectedContent",
        line.no,
        indentOf(line.text) + 1
      );
    }
    return value;
  }

  // -- errors and warnings ---------------------------------------------------

  private error(
    code: string,
    line: Line,
    column: number,
    params: Params = {}
  ): never {
    throw new DataError(code, line.no, column, params);
  }

  warn(code: string, line: number, params: Params = {}): void {
    this.warnings.push({ code, line, params });
  }

  /** An error located by an offset into text that began on `line` at column `col`. */
  private failer(text: string, line: Line, col: number): Fail {
    return (code, offset, params = {}) => {
      const rows = text.slice(0, offset).split("\n");
      const last = rows.length - 1;
      const column = last === 0 ? col + offset : rows[last].length + 1;
      throw new DataError(code, line.no + last, column, params);
    };
  }

  // -- lines -----------------------------------------------------------------

  private skipBlank(): void {
    while (
      this.pos < this.lines.length &&
      (isBlankLine(this.lines[this.pos].text) ||
        isCommentLine(this.lines[this.pos].text))
    ) {
      this.pos++;
    }
  }

  private checkIndent(line: Line): number {
    const indent = indentOf(line.text);
    if (line.text[indent] === "\t" && !isBlankLine(line.text)) {
      this.error("tabIndent", line, indent + 1);
    }
    return indent;
  }

  // -- blocks ----------------------------------------------------------------

  private parseBlock(parent: number): DataValue {
    this.skipBlank();
    if (this.pos >= this.lines.length) {
      return null;
    }
    const line = this.lines[this.pos];
    const n = this.checkIndent(line);
    if (n <= parent) {
      return null;
    }
    const content = line.text.slice(n);
    if (isItem(content)) {
      return this.parseSequence(n);
    }
    if (isExplicitKey(content)) {
      return this.parseMapping(n);
    }
    const props = parseProps(content);
    if (props.anchor !== null || props.tag !== null) {
      return this.parseWithProps(props, parent, line, n);
    }
    if (mappingColon(content) >= 0) {
      return this.parseMapping(n);
    }
    return this.parseNodeText(content, parent, line, n + 1, null, true);
  }

  /** A block that starts with an anchor or a tag. */
  private parseWithProps(
    props: Props,
    parent: number,
    line: Line,
    n: number
  ): DataValue {
    if (props.rest === "" || props.rest.startsWith("#")) {
      // Properties on a line of their own apply to the block below.
      this.pos++;
      return this.finish(this.parseBlock(parent), props, line, n + 1, false);
    }
    if (mappingColon(props.rest) >= 0) {
      // Properties before an implicit key belong to the key, which this parser cannot hold.
      this.warn("propsOnKey", line.no);
      this.lines[this.pos] = {
        text: " ".repeat(n + props.used) + props.rest,
        no: line.no,
      };
      return this.parseMapping(n + props.used);
    }
    return this.parseNodeText(
      props.rest,
      parent,
      line,
      n + props.used + 1,
      props
    );
  }

  private parseSequence(n: number): DataValue[] {
    const items: DataValue[] = [];
    for (;;) {
      this.skipBlank();
      if (this.pos >= this.lines.length) {
        break;
      }
      const line = this.lines[this.pos];
      const indent = this.checkIndent(line);
      if (indent < n) {
        break;
      }
      if (indent > n) {
        this.error("badIndent", line, indent + 1);
      }
      const content = line.text.slice(indent);
      if (!isItem(content)) {
        break;
      }
      items.push(this.parseItem(line, n, content));
    }
    return items;
  }

  private parseItem(line: Line, n: number, content: string): DataValue {
    const after = content.slice(1);
    const rest = after.trimStart();
    const gap = after.length - rest.length;
    if (rest === "" || rest.startsWith("#")) {
      this.pos++;
      return this.parseBlock(n);
    }
    // The item's content starts on this line: parse the rest as a line indented to it.
    this.lines[this.pos] = {
      text: " ".repeat(n + 1 + gap) + rest,
      no: line.no,
    };
    return this.parseBlock(n);
  }

  private parseMapping(n: number): DataMap {
    const map: DataMap = new Map();
    const firstSeen = new Map<string, number>();
    const merged = new Set<string>();
    for (;;) {
      this.skipBlank();
      if (this.pos >= this.lines.length) {
        break;
      }
      const line = this.lines[this.pos];
      const indent = this.checkIndent(line);
      if (indent < n) {
        break;
      }
      if (indent > n) {
        this.error("badIndent", line, indent + 1);
      }
      const content = line.text.slice(n);
      if (isItem(content)) {
        break;
      }
      this.parseEntry(map, firstSeen, merged, line, n, content);
    }
    return map;
  }

  private parseEntry(
    map: DataMap,
    firstSeen: Map<string, number>,
    merged: Set<string>,
    line: Line,
    n: number,
    content: string
  ): void {
    let key: string;
    let value: DataValue;
    let quotedKey = false;
    if (isExplicitKey(content)) {
      key = this.parseExplicitKey(line, n, content);
      value = this.parseExplicitValue(n);
    } else {
      const colon = mappingColon(content);
      if (colon < 0) {
        this.error("noColon", line, n + 1);
      }
      const keyText = content.slice(0, colon).trimEnd();
      quotedKey = keyText.startsWith('"') || keyText.startsWith("'");
      key = this.parseKey(keyText, line, n + 1);
      value = this.parseValueAfterColon(
        content.slice(colon + 1),
        n,
        line,
        n + colon + 2
      );
    }
    if (key === "<<" && this.options.merge && !quotedKey) {
      this.merge(map, merged, value, line);
      return;
    }
    if (map.has(key) && !merged.has(key)) {
      this.error("duplicateKey", line, n + 1, {
        key,
        first: firstSeen.get(key) ?? 0,
      });
    }
    merged.delete(key);
    firstSeen.set(key, line.no);
    map.set(key, value);
  }

  /** `? key`: the key is a scalar written the long way (it may span lines). */
  private parseExplicitKey(line: Line, n: number, content: string): string {
    const after = content.slice(1);
    const rest = after.trimStart();
    const gap = after.length - rest.length;
    let node: DataValue;
    if (rest === "" || rest.startsWith("#")) {
      this.pos++;
      node = this.parseBlock(n);
    } else {
      this.lines[this.pos] = {
        text: " ".repeat(n + 1 + gap) + rest,
        no: line.no,
      };
      node = this.parseBlock(n);
    }
    return keyToString(node) ?? this.error("complexKey", line, n + 1);
  }

  /** The `: value` line that answers a `? key`; a missing one means null. */
  private parseExplicitValue(n: number): DataValue {
    this.skipBlank();
    const line = this.lines[this.pos];
    if (line === undefined || this.checkIndent(line) !== n) {
      return null;
    }
    const content = line.text.slice(n);
    const next = content[1];
    if (
      content[0] !== ":" ||
      !(next === undefined || next === " " || next === "\t")
    ) {
      return null;
    }
    const after = content.slice(1);
    const rest = after.trimStart();
    if (rest === "" || rest.startsWith("#")) {
      this.pos++;
      return this.parseNestedValue(n);
    }
    // The value starts on this line, possibly as a mapping or a list of its own.
    const gap = after.length - rest.length;
    this.lines[this.pos] = {
      text: " ".repeat(n + 1 + gap) + rest,
      no: line.no,
    };
    return this.parseBlock(n);
  }

  private parseKey(text: string, line: Line, col: number): string {
    if (text.startsWith('"') || text.startsWith("'")) {
      const fail = this.failer(text, line, col);
      const quoted =
        text[0] === '"'
          ? readDoubleQuoted(text, 0, fail)
          : readSingleQuoted(text, 0);
      return quoted ? quoted.value : this.error("unterminatedQuote", line, col);
    }
    const key = keyToString(resolvePlain(text).value);
    return key ?? this.error("complexKey", line, col);
  }

  private merge(
    map: DataMap,
    merged: Set<string>,
    value: DataValue,
    line: Line
  ): void {
    const sources = Array.isArray(value) ? value : [value];
    const maps: DataMap[] = [];
    for (const source of sources) {
      if (!isMap(source)) {
        this.error("mergeNotMap", line, 1);
      }
      maps.push(source as DataMap);
    }
    for (const source of maps) {
      for (const [k, v] of source) {
        if (!map.has(k)) {
          map.set(k, v);
          merged.add(k);
        }
      }
    }
  }

  private parseValueAfterColon(
    rest: string,
    n: number,
    line: Line,
    col: number
  ): DataValue {
    const props = parseProps(rest);
    if (props.rest === "" || props.rest.startsWith("#")) {
      this.pos++;
      const nested = this.parseNestedValue(n);
      // An empty value with !!str is the empty string, not null.
      const value =
        nested === null && coreTag(props.tag) === "str" ? "" : nested;
      return this.finish(value, props, line, col, false);
    }
    return this.parseNodeText(props.rest, n, line, col + props.used, props);
  }

  /** The value of `key:` when nothing follows the colon: the block below, if any. */
  private parseNestedValue(n: number): DataValue {
    this.skipBlank();
    if (this.pos >= this.lines.length) {
      return null;
    }
    const line = this.lines[this.pos];
    const indent = this.checkIndent(line);
    if (indent > n) {
      return this.parseBlock(n);
    }
    return indent === n && isItem(line.text.slice(indent))
      ? this.parseSequence(n)
      : null;
  }

  // -- inline nodes -------------------------------------------------------------

  /** A node that starts in the middle of a line: `text` is what follows any properties. */
  private parseNodeText(
    text: string,
    parent: number,
    line: Line,
    col: number,
    props: Props | null,
    blockLevel = false
  ): DataValue {
    const clean = stripComment(text);
    const first = clean[0];
    let value: DataValue;
    let collection = false;
    if (first === "*") {
      value = this.parseAlias(clean, line, col, props);
    } else if (first === "|" || first === ">") {
      value = this.parseBlockScalar(clean, parent, line, col, props);
    } else if (first === '"' || first === "'") {
      value = this.parseQuoted(text, line, col, props);
    } else if (first === "[" || first === "{") {
      value = this.parseFlowNode(text, line, col);
      collection = true;
    } else {
      value = this.parsePlain(clean, parent, line, col, props, blockLevel);
    }
    return props ? this.finish(value, props, line, col, collection) : value;
  }

  private parseAlias(
    text: string,
    line: Line,
    col: number,
    props: Props | null
  ): DataValue {
    if (!RE_ALIAS.test(text) || props?.anchor) {
      this.error("unexpectedContent", line, col);
    }
    const name = text.slice(1);
    const value = this.anchors.get(name);
    if (value === undefined) {
      this.error("unknownAlias", line, col, { name });
    }
    this.pos++;
    return value as DataValue;
  }

  private parseQuoted(
    text: string,
    line: Line,
    col: number,
    props: Props | null
  ): DataValue {
    let source = text;
    while (quotedEnd(source, 0) < 0) {
      this.pos++;
      if (this.pos >= this.lines.length) {
        return this.error("unterminatedQuote", line, col);
      }
      source += `\n${this.lines[this.pos].text}`;
    }
    const fail = this.failer(source, line, col);
    const quoted =
      source[0] === '"'
        ? readDoubleQuoted(source, 0, fail)
        : readSingleQuoted(source, 0);
    if (!quoted) {
      return this.error("unterminatedQuote", line, col);
    }
    if (stripComment(source.slice(quoted.end)) !== "") {
      this.error("unexpectedContent", line, col + quoted.end);
    }
    this.pos++;
    return this.makeScalar(quoted.value, true, props?.tag ?? null, line);
  }

  private parseFlowNode(text: string, line: Line, col: number): DataValue {
    let source = text;
    while (!flowComplete(source)) {
      this.pos++;
      if (this.pos >= this.lines.length) {
        return this.error("unterminatedFlow", line, col);
      }
      source += `\n${this.lines[this.pos].text}`;
    }
    const value = new FlowReader(source, line, col, this).parseDocument();
    this.pos++;
    return value;
  }

  private parsePlain(
    text: string,
    parent: number,
    line: Line,
    col: number,
    props: Props | null,
    blockLevel: boolean
  ): DataValue {
    this.checkPlainStart(text, line, col);
    const colon = mappingColon(text);
    if (colon >= 0) {
      this.error("mappingValues", line, col + colon);
    }
    const parts = [text];
    this.pos++;
    let blanks = 0;
    let scan = this.pos;
    while (scan < this.lines.length) {
      const next = this.lines[scan];
      if (isBlankLine(next.text)) {
        blanks++;
        scan++;
        continue;
      }
      const indent = indentOf(next.text);
      const trimmed = next.text.trim();
      const marker =
        indent === 0 &&
        (RE_DOC_START.test(next.text) || RE_DOC_END.test(next.text));
      if (indent <= parent || trimmed.startsWith("#") || marker) {
        break;
      }
      const cleaned = stripComment(trimmed);
      const inner = mappingColon(cleaned);
      if (inner >= 0 && blockLevel) {
        // A line that wanted to be a key but has no colon of its own.
        this.error("noColon", line, col);
      }
      if (inner >= 0) {
        this.error("mappingValues", next, indent + 1 + inner);
      }
      parts.push(blanks > 0 ? "\n".repeat(blanks) : " ", cleaned);
      blanks = 0;
      scan++;
      this.pos = scan;
    }
    return this.makeScalar(parts.join(""), false, props?.tag ?? null, line);
  }

  private checkPlainStart(text: string, line: Line, col: number): void {
    const c = text[0];
    const reserved = c === "@" || c === "`" || c === "%";
    const indicator = c === "]" || c === "}" || c === ",";
    const lone =
      (c === "-" || c === "?" || c === ":") &&
      (text.length === 1 || text[1] === " ");
    if (reserved || indicator || lone) {
      this.error("badCharacter", line, col, { char: c });
    }
  }

  private parseBlockScalar(
    header: string,
    parent: number,
    line: Line,
    col: number,
    props: Props | null
  ): DataValue {
    const parsed = parseBlockHeader(header);
    if (!parsed) {
      return this.error("badBlockHeader", line, col, { text: header });
    }
    this.pos++;
    // The indicator counts from the parent's indentation (-1 at the top of a document).
    const explicit = parsed.indent > 0 ? parent + parsed.indent : -1;
    const indent = explicit >= 0 ? explicit : this.detectBlockIndent(parent);
    const collected: string[] = [];
    while (indent >= 0 && this.pos < this.lines.length) {
      const current = this.lines[this.pos];
      if (isBlankLine(current.text)) {
        collected.push(current.text.slice(indent));
      } else {
        const lead = indentOf(current.text);
        const marker =
          lead === 0 &&
          (RE_DOC_START.test(current.text) || RE_DOC_END.test(current.text));
        if (lead < indent || marker) {
          break;
        }
        collected.push(current.text.slice(indent));
      }
      this.pos++;
    }
    const text = assembleBlock(collected, parsed.folded, parsed.chomp);
    return this.makeScalar(text, true, props?.tag ?? null, line);
  }

  /** The indentation of a block scalar's first non-blank line; -1 when there is none. */
  private detectBlockIndent(parent: number): number {
    for (let k = this.pos; k < this.lines.length; k++) {
      if (!isBlankLine(this.lines[k].text)) {
        const lead = indentOf(this.lines[k].text);
        return lead > parent ? lead : -1;
      }
    }
    return -1;
  }

  // -- scalars, tags, anchors -------------------------------------------------------

  /** A scalar's value from its text, by the core schema, a tag, or its quoting. */
  makeScalar(
    text: string,
    quoted: boolean,
    tag: string | null,
    line: Line
  ): DataValue {
    const core = coreTag(tag);
    if (core === "str" || tag === "!" || (quoted && core === null)) {
      return text;
    }
    const resolved = resolvePlain(text);
    if (resolved.kind === "str" && quoted) {
      return text;
    }
    this.noteScalar(text, resolved, line);
    return core === null
      ? resolved.value
      : this.coerce(core, text, resolved, line);
  }

  private coerce(
    tag: string,
    text: string,
    resolved: ResolvedScalar,
    line: Line
  ): DataValue {
    const { kind, value } = resolved;
    const fits =
      (tag === "int" && kind === "int") ||
      (tag === "float" && (kind === "int" || kind === "float")) ||
      (tag === "bool" && kind === "bool") ||
      (tag === "null" && kind === "null");
    if (!fits) {
      this.error("badTag", line, 1, { tag: `!!${tag}`, text });
    }
    return tag === "float" && typeof value === "bigint" ? Number(value) : value;
  }

  private noteScalar(text: string, resolved: ResolvedScalar, line: Line): void {
    if (resolved.yaml11) {
      this.warn("yaml11", line.no, { value: text });
    }
    if (resolved.nonFinite) {
      this.warn("nonFinite", line.no, { value: text });
    }
  }

  /** Check a collection against its tag, warn about an unknown tag, record the anchor. */
  private finish(
    value: DataValue,
    props: Props,
    line: Line,
    col: number,
    collection: boolean
  ): DataValue {
    this.applyProps(props.tag, props.anchor, value, line, col, collection);
    return value;
  }

  // -- FlowHost ------------------------------------------------------------------------

  lookupAnchor(name: string): DataValue | undefined {
    return this.anchors.get(name);
  }

  applyProps(
    tag: string | null,
    anchor: string | null,
    value: DataValue,
    line: Line,
    col: number,
    collection: boolean
  ): void {
    const core = coreTag(tag);
    if (tag !== null && core === null && tag !== "!") {
      this.warn("unknownTag", line.no, { tag });
    }
    if (collection && core === "seq" && !Array.isArray(value)) {
      this.error("badTag", line, col, { tag: "!!seq", text: "" });
    }
    if (collection && core === "map" && !isMap(value)) {
      this.error("badTag", line, col, { tag: "!!map", text: "" });
    }
    if (anchor !== null) {
      this.anchors.set(anchor, value);
    }
  }
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

function hasContent(lines: Line[]): boolean {
  return lines.some((l) => !(isBlankLine(l.text) || isCommentLine(l.text)));
}

function splitDocuments(text: string): Line[][] {
  const all = text.replace(RE_BOM, "").replace(RE_NEWLINES, "\n").split("\n");
  if (all.at(-1) === "") {
    // The break that ends the last line does not start another one.
    all.pop();
  }
  const docs: Line[][] = [];
  let current: Line[] | null = null;
  let explicit = false;
  const close = () => {
    if (current && (explicit || hasContent(current))) {
      docs.push(current);
    }
    current = null;
    explicit = false;
  };
  all.forEach((raw, index) => {
    const line: Line = { text: raw, no: index + 1 };
    if (current === null && raw.startsWith("%")) {
      if (!RE_VERSION.test(raw)) {
        throw new DataError("unsupportedDirective", line.no, 1, {
          text: raw.split(RE_WHITESPACE)[0],
        });
      }
    } else if (RE_DOC_START.test(raw)) {
      close();
      current = [];
      explicit = true;
      const rest = raw.slice(3).trim();
      if (rest !== "" && !rest.startsWith("#")) {
        current.push({ text: rest, no: line.no });
      }
    } else if (RE_DOC_END.test(raw)) {
      close();
    } else {
      current = current ?? [];
      current.push(line);
    }
  });
  close();
  return docs;
}

/** Parse YAML text into one value per document. Throws a `DataError` with line and column. */
export function parseYaml(
  text: string,
  options: YamlParseOptions
): YamlParseResult {
  const warnings: DataWarning[] = [];
  const documents = splitDocuments(text).map((lines) =>
    new DocumentParser(lines, options, warnings).parse()
  );
  return { documents, warnings };
}
